import type { FieldError as ReactHookFormFieldError } from 'react-hook-form';
import type { CompanyManageableRole } from '@shared/schemas/organization';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

const roleOptions = [
  {
    description: 'Accède aux parcours métier sans pouvoir administrer l’entreprise.',
    label: 'Membre',
    value: 'member',
  },
  {
    description: 'Gère les membres, les sources et les paramètres de l’entreprise.',
    label: 'Administrateur de l’entreprise',
    value: 'admin',
  },
] as const;

interface CompanyMemberRoleFieldProps {
  error?: ReactHookFormFieldError;
  onChange: (role: CompanyManageableRole) => void;
  value: CompanyManageableRole;
}

export function CompanyMemberRoleField({
  error,
  onChange,
  value,
}: CompanyMemberRoleFieldProps) {
  return (
    <FieldSet data-invalid={Boolean(error)}>
      <FieldLegend id="company-member-role-label" variant="label">
        Rôle dans l’entreprise
      </FieldLegend>
      <RadioGroup
        aria-describedby={error ? 'company-member-role-error' : undefined}
        aria-invalid={Boolean(error)}
        aria-labelledby="company-member-role-label"
        className="grid gap-3 md:grid-cols-2"
        onValueChange={(nextValue) => onChange(nextValue as CompanyManageableRole)}
        value={value}
      >
        {roleOptions.map((option) => (
          <FieldLabel
            className={cn(
              'cursor-pointer',
              value === option.value && 'border-primary bg-primary/5',
            )}
            htmlFor={`company-member-role-${option.value}`}
            key={option.value}
          >
            <Field orientation="horizontal">
              <RadioGroupItem
                id={`company-member-role-${option.value}`}
                value={option.value}
              />
              <span>
                <span className="block font-medium">{option.label}</span>
                <FieldDescription>{option.description}</FieldDescription>
              </span>
            </Field>
          </FieldLabel>
        ))}
      </RadioGroup>
      <FieldError id="company-member-role-error" errors={[error]} />
    </FieldSet>
  );
}
